import Avatar from '@ahamove/web-components/lib/Avatar';
import Box from '@ahamove/web-components/lib/Box';
import cn from 'classnames';
import 'keen-slider/keen-slider.min.css';
import type { KeenSliderPlugin } from 'keen-slider/react';
import { useKeenSlider } from 'keen-slider/react';
import { useEffect, useState } from 'react';
import type { ServiceModalData } from '@/api/service/types';
import ButtonNavSwiper from '@/shared-components/ButtonNavSwiper';
import useIsMobile from '@/shared-hooks/useIsMobile';
import ServiceDescriptionItem from './ServiceDescriptionItem';

const MutationPlugin: KeenSliderPlugin = (slider) => {
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      slider.update();
    });
  });
  const config = { childList: true };

  slider.on('created', () => {
    observer.observe(slider.container, config);
  });
  slider.on('destroyed', () => {
    observer.disconnect();
  });
};

type Props = {
  listService: ServiceModalData[];
  currentSlide: number;
  changeCurrentSlide: (value: number) => void;
  shouldRefresh?: boolean;
};
export default function ServicePriceSlider({
  listService,
  currentSlide,
  changeCurrentSlide,
  shouldRefresh = false,
}: Props) {
  const [loaded, setLoaded] = useState(false);
  const isMobile = useIsMobile();
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      initial: 0,
      loop: isMobile
        ? listService.length > 2
          ? true
          : false
        : listService.length > 2
        ? true
        : false,
      created() {
        setLoaded(true);
      },
      slideChanged(slider) {
        changeCurrentSlide(slider.track.details.rel);
      },
      slides: {
        perView: isMobile
          ? listService.length > 2
            ? 3
            : listService.length === 2
            ? 2
            : 1
          : listService.length > 3
          ? 5
          : 3,
        spacing: isMobile ? 16 : 24,
        origin: 'center',
      },
    },
    [MutationPlugin]
  );

  useEffect(() => {
    if (instanceRef.current && shouldRefresh) {
      instanceRef.current.update();
    }
  }, [shouldRefresh]);

  return (
    <>
      <div
        className={cn('desktop:px-16', {
          'navigation-wrapper': !isMobile && listService.length > 1,
        })}
      >
        <div
          className={cn('desktop:px-28 desktop:pt-3 flex flex-row pt-2', {
            'overflow-hidden':
              listService.length > 3 || (isMobile && listService.length > 1),
          })}
        >
          <div
            ref={sliderRef}
            className={cn('keen-slider pb-10', {
              'px-14': isMobile && listService.length < 2,
              '-mx-[400px] !w-[calc(100%+800px)]':
                !isMobile && listService.length > 3,
              '-mx-[250px] !w-[calc(100%+500px)]':
                isMobile && listService.length > 2,
              '-mx-[25%] !w-[calc(150%)]': isMobile && listService.length === 2,
            })}
          >
            {listService.map((item, idx) => (
              <Box
                key={idx}
                className={cn(
                  `keen-slider__slide number-slide${idx} desktop:!min-h-[346px] !min-h-[292px]`,
                  {
                    'active shadow-dropshadow':
                      instanceRef?.current?.track.details.rel === idx,
                    '!min-w-full': isMobile && listService.length === 1,
                    // '!min-w-[270px] !max-w-[270px]':
                    //   isMobile && listService.length === 2,
                  }
                )}
                sx={{
                  transition: 'padding .25s linear',
                  '&.active': {
                    backgroundColor: '#FCFCFD',
                    borderRadius: 4,
                    py: '0 !important',
                  },
                }}
              >
                <Box
                  className={cn({
                    active: instanceRef?.current?.track.details.rel === idx,
                  })}
                  px={1.5}
                  py={2}
                  sx={{
                    transform: 'scale(0.9) !important',
                    height: '100% !important',
                    backgroundColor: '#FCFCFD',
                    borderRadius: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.7,
                    '&.active': {
                      transform: 'scale(1) !important',
                      opacity: 1,
                    },
                  }}
                >
                  <Avatar
                    variant="rounded"
                    src={item.photos[0]}
                    sx={{
                      height: 'auto !important',
                      width: '100% !important',
                      aspectRatio: '1',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: 'transparent',
                      '.MuiAvatar-img': {
                        objectFit: 'contain ',
                      },
                    }}
                    imgProps={{ height: 'auto' }}
                  />
                  <h3 className="text-subtitle20 text-neutral-90 mt-4 text-center font-bold">
                    {item?.vehicle_model?.name}
                  </h3>
                </Box>
              </Box>
            ))}
          </div>
        </div>
        {!isMobile &&
          listService.length > 1 &&
          loaded &&
          instanceRef.current && (
            <>
              <ButtonNavSwiper
                left
                onClick={() => instanceRef.current?.prev()}
                loop={listService.length > 2}
                disabled={instanceRef?.current?.track.details.rel === 0}
              />

              <ButtonNavSwiper
                onClick={() => instanceRef.current?.next()}
                loop={listService.length > 2}
                disabled={
                  instanceRef?.current?.track.details.rel ===
                  instanceRef.current.track.details.slides.length - 1
                }
              />
            </>
          )}
      </div>

      {instanceRef?.current?.track.details.rel !== undefined ? (
        <ServiceDescriptionItem
          service={listService[instanceRef?.current?.track.details.rel]}
        />
      ) : null}
    </>
  );
}
function useeffect(arg0: () => void, arg1: never[]) {
  throw new Error('Function not implemented.');
}
